'use client';

import { Card } from '@/components/Card';
import { IHomeDictionary } from '@/dictionaries/pages/home/types';
import { VillasCount } from '@/services/types';
import { regionCardsImages } from '@/utils';
import parser, {
  DOMNode,
  HTMLReactParserOptions,
  domToReact,
} from 'html-react-parser';
import Link from 'next/link';
import React, { HTMLAttributes, useMemo } from 'react';
import './styles.css';

import sicilyImg from '../../../../public/home-destinations-sicily.jpg';
import pugliaImg from '../../../../public/home-destinations-puglia.jpg';

interface IHomeRegionsProps extends HTMLAttributes<HTMLDivElement> {
  dict: IHomeDictionary;
  lang: string;
  villasCount: VillasCount[];
}

// const order = ['north', 'west', 'east', 'south'];
const regionData = [
  {
    img: sicilyImg,
  },
  { img: pugliaImg },
]; // [sicily, puglia]

export const HomeRegions: React.FC<IHomeRegionsProps> = ({
  dict,
  lang,
  villasCount,
}) => {
  const parseOptions: HTMLReactParserOptions = {
    replace: (node, index) => {
      if (node.type === 'tag' && node.name === 'a' && node.children) {
        const href = node.attribs?.href || '/';
        return (
          <Link
            className="styled-link"
            href={lang === 'en' ? `${href || '/'}` : `/${lang}${href || '/'}`}
            key={index}>
            <strong>
              {domToReact(node.children as DOMNode[], parseOptions)}
            </strong>
          </Link>
        );
      }
    },
  };

  let sicilyCount = 0;
  let pugliaCount = 0;

  if (villasCount) {
    villasCount.forEach((villa) => {
      if (villa.zone_code.startsWith('sicily')) {
        sicilyCount += villa.number_of_villas;
      } else if (villa.zone_code.startsWith('puglia')) {
        pugliaCount += villa.number_of_villas;
      }
    });
  }

  const renderRegionCards = useMemo(
    () =>
      dict.locations.cards.map((card, index) => {
        return (
          <div
            key={index}
            className="desktop:flex-auto px-0 tablet:px-3 desktop:px-0">
            <Card
              lang={lang}
              key={index}
              cardImage={regionData[index].img}
              cardAlt={card.region}
              customStyle="flex-auto flex h-full before:h-[40%] aspect-[7/5] group"
              customContentStyle="absolute bottom-0 z-10 w-full p-6 pt-8 bg-gradient-to-t from-black/70 via-black/60 via-80% to-transparent"
              sizes="(min-width: 640px) 50vw, (min-width: 800px) 33vw, (min-width: 1200px) 25vw, 100vw"
              href={card.href}
              hoverContent={
                <div className="flex items-center gap-2 h-0 group-hover:h-5 transition-[height]">
                  <span className="w-6 h-[2px] bg-[#ebbf20] text-sm" />
                  <span className="tracking-widest text-sm uppercase">
                    {dict.locations.discover} {card.region}
                  </span>
                </div>
              }>
              <div className="flex flex-row items-center h-fit">
                <div className="flex flex-col flex-1">
                  <p className="text-[32px] leading-10 font-noe tracking-wider w-full box-border whitespace-normal pb-2">
                    <strong>{card.region}</strong>
                  </p>
                  <p className="mb-2 opacity-85">
                    {index === 0 ? sicilyCount : pugliaCount}{' '}
                    {dict.locations.villas}
                  </p>
                  <p className="text-base w-full box-border whitespace-normal leading-[26px] hidden tablet:block">
                    {card.description}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        );
      }),
    [
      dict.locations.cards,
      dict.locations.discover,
      dict.locations.villas,
      lang,
      pugliaCount,
      sicilyCount,
    ],
  );

  return (
    <section className="max-w-default pt-32 mx-auto relative z-10">
      <div className="absolute bg-gray-100 w-[120vw] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-1/2 z-0 -rotate-6" />
      <div className="z-10 relative">
        <h2 className="text-title font-title font-bold w-full text-center">
          <span className="styled-link">
            {parser(dict.locations.title, parseOptions)}
          </span>
        </h2>
        <p className="mt-5 mb-20 text-gray-850 text-body text-center max-w-[90%] mx-auto">
          {parser(dict.locations.subtitle, parseOptions)}
        </p>
      </div>
      <div className="flex flex-col desktop:flex-row gap-6">
        {renderRegionCards}
      </div>
    </section>
  );
};
